/**
 * Mobile Menu Component
 * Slide-out navigation menu for mobile devices
 * 
 * UI Design Pattern:
 * - Sheet component (slide from right)
 * - Full navigation items with icons
 * - User profile section at top
 * - Logout button at bottom
 * - Teal accent colors (#026857)
 */

import { Link, useLocation } from 'react-router-dom';
import { X, LogOut } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/components/ui/avatar';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/shared/components/ui/sheet';
import type { AuthUser } from '@/core/auth/types';
import type { NavigationItem } from '../config/navigationItems';
import { BetaBadge } from '@/shared/components/BetaBadge';

/**
 * Renders navigation item badge based on badge type
 */
const renderNavBadge = (badge: string | undefined) => {
  if (!badge) return null;

  switch (badge) {
    case 'beta':
      return <BetaBadge />;
    default:
      return (
        <span className="px-2 py-0.5 text-xs rounded-full bg-red-500 text-white">
          {badge}
        </span>
      );
  }
};

type MobileMenuProps = {
  isOpen: boolean;
  onClose: () => void;
  navItems: NavigationItem[];
  user: AuthUser;
  onLogout: () => void;
};

export const MobileMenu = ({ isOpen, onClose, navItems, user, onLogout }: MobileMenuProps) => {
  const location = useLocation();

  // Check if a route is active
  const isActive = (href: string) => location.pathname === href;

  // Get user initials for avatar fallback
  const getUserInitials = (firstName: string, lastName?: string) => {
    if (lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    }
    return firstName.slice(0, 2).toUpperCase();
  };

  // Get full name from firstName and lastName
  const getFullName = (firstName: string, lastName?: string) => {
    return [firstName, lastName].filter(Boolean).join(' ');
  };

  // Handle navigation item click
  const handleNavClick = () => {
    onClose();
  };

  // Handle logout
  const handleLogout = () => {
    onClose();
    onLogout();
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="right" className="w-80 p-0" showCloseButton={false}>
        {/* Header with User Profile */}
        <SheetHeader className="border-b p-6 bg-gradient-to-br from-[#026857] to-[#378F94]">
          <div className="flex items-center justify-between mb-4">
            <SheetTitle className="text-white text-lg">Menu</SheetTitle>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="text-white hover:bg-white/20"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
          
          <div className="flex items-center gap-3 pt-2">
            <Avatar className="h-12 w-12 border-2 border-white">
              {user.profilePhoto && (
                <AvatarImage src={user.profilePhoto} alt={getFullName(user.firstName, user.lastName)} />
              )}
              <AvatarFallback className="bg-white text-[#026857] font-semibold">
                {getUserInitials(user.firstName, user.lastName)}
              </AvatarFallback>
            </Avatar>
            <div className="flex flex-col items-start text-white">
              <span className="font-medium">{getFullName(user.firstName, user.lastName)}</span>
              <span className="text-sm text-white/80">{user.email}</span>
            </div>
          </div>
        </SheetHeader>

        {/* Navigation Items */}
        <nav className="flex flex-col p-4 gap-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.href);
            
            return (
              <Link
                key={`${item.label}-${item.href}`}
                to={item.href}
                onClick={handleNavClick}
                className={`flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition-colors ${
                  active
                    ? 'bg-[#026857]/10 text-[#026857]'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="flex-1">{item.label}</span>
                {renderNavBadge(item.badge)}
              </Link>
            );
          })}
        </nav>

        {/* Logout Button at Bottom */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t bg-white">
          <Button
            variant="outline"
            className="w-full justify-start text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
            onClick={handleLogout}
          >
            <LogOut className="mr-2 h-4 w-4" />
            <span>Logout</span>
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

